import {
  createActionsHook,
  createHook,
  createStateHook,
  createStore,
} from "react-sweet-state";
import { LoadStatus, Notification } from "types/types";
import {
  InsurerReport,
  InsurerReportEntry,
  InsurerReportEntryMetadataMap,
  InsurerReportMetadataMap,
} from "types/insurerReports";
import {
  applyInsurerReport,
  applyInsurerReportEntry,
  deleteInsurerReport,
  filterInsurerReportEntries,
  filterInsurerReports,
  getMatchedTargetInfo,
  importInsurerReport,
  loadInsurerReportEntries,
  loadInsurerReports,
  matchInsurerReport,
  matchInsurerReportEntry,
  removeInsurerReports,
  selectInsurerReport,
  setStoreValue,
  setStoreValues,
} from "./InsurerReportsStoreActions";
import moment from "moment";
import { setNotificationMessage } from "./AppStoreActions";

export type StoreState = {
  notifications: Notification[];
  insurerReports: InsurerReport[];
  insurerReportEntries: InsurerReportEntry[];
  insurerReportMetadataMap: InsurerReportMetadataMap;
  insurerReportEntryMetadataMap: InsurerReportEntryMetadataMap;
  // master/detail: only one report can be selected; insurerReportEntries holds
  // the entries of that report only (loaded on selection)
  selectedInsurerReportId: number | null;

  insurerReportsLoadStatus: LoadStatus;
  insurerReportEntriesLoadStatus: LoadStatus;
  deleteInsurerReportStatus: LoadStatus;
  matchInsurerReportStatus: LoadStatus;
  importInsurerReportStatus: LoadStatus;
  matchInsurerReportEntryStatus: LoadStatus;
  infoInsurerReportEntryStatus: LoadStatus;

  searchTerm: string; // master grid (reports)
  entriesSearchTerm: string; // detail grid (entries of the selected report)
  fromDateFilter: Date;
  toDateFilter: Date;
  searchDateFilterField: "insurerReportDate" | "insurerReportCreatedAt";
  searchInsurersSelectedItem: any;
  includePerfectMatches: boolean;

  insurerReportsSort: any;
  insurerReportEntriesSort: any;

  importInsurerReportDialog: {
    isOpen: boolean;
  };
  deleteInsurerReportDialog: {
    isOpen: boolean;
    insurerReportId: any;
  };
  matchInsurerReportDialog: {
    isOpen: boolean;
    insurerReportId: any;
  };
  matchInsurerReportEntryDialog: {
    isOpen: boolean;
    insurerReportEntryId: any;
  };
  infoInsurerReportEntryDialog: {
    isOpen: boolean;
    insurerReportEntryId: any;
  };
  infoMatchedTarget: any;
  infoMatchedTargetKind: "payment" | "paymentPlanEntry" | null;
};

const initialState: StoreState = {
  notifications: [],
  insurerReports: [],
  insurerReportEntries: [],
  insurerReportMetadataMap: {},
  insurerReportEntryMetadataMap: {},
  selectedInsurerReportId: null,

  insurerReportsLoadStatus: "none",
  insurerReportEntriesLoadStatus: "none",
  deleteInsurerReportStatus: "none",
  matchInsurerReportStatus: "none",
  importInsurerReportStatus: "none",
  matchInsurerReportEntryStatus: "none",
  infoInsurerReportEntryStatus: "none",

  searchTerm: "",
  entriesSearchTerm: "",
  fromDateFilter: moment().subtract(3, "months").startOf("day").toDate(),
  toDateFilter: moment().toDate(),
  searchDateFilterField: "insurerReportDate",
  searchInsurersSelectedItem: null,
  // Perfect (automatic) matches are the orange entry rows; shown by default.
  includePerfectMatches: JSON.parse(
    localStorage.getItem("insurerReportsIncludePerfectMatches") ?? "true"
  ),

  insurerReportsSort: null,
  insurerReportEntriesSort: null,

  importInsurerReportDialog: {
    isOpen: false,
  },
  deleteInsurerReportDialog: {
    isOpen: false,
    insurerReportId: null,
  },
  matchInsurerReportDialog: {
    isOpen: false,
    insurerReportId: null,
  },
  matchInsurerReportEntryDialog: {
    isOpen: false,
    insurerReportEntryId: null,
  },
  infoInsurerReportEntryDialog: {
    isOpen: false,
    insurerReportEntryId: null,
  },
  infoMatchedTarget: null,
  infoMatchedTargetKind: null,
};

const actions = {
  setInsurerReportsStoreValue: setStoreValue,
  setInsurerReportsStoreValues: setStoreValues,
  loadInsurerReports: loadInsurerReports,
  loadInsurerReportEntries: loadInsurerReportEntries,
  selectInsurerReport: selectInsurerReport,
  filterInsurerReports: filterInsurerReports,
  filterInsurerReportEntries: filterInsurerReportEntries,
  deleteInsurerReport: deleteInsurerReport,
  matchInsurerReport: matchInsurerReport,
  importInsurerReport: importInsurerReport,
  matchInsurerReportEntry: matchInsurerReportEntry,
  applyInsurerReport: applyInsurerReport,
  applyInsurerReportEntry: applyInsurerReportEntry,
  removeInsurerReports: removeInsurerReports,
  getMatchedTargetInfo: getMatchedTargetInfo,
  setNotificationMessage: setNotificationMessage,
};

const InsurerReportsStore = createStore({
  initialState,
  actions,
  name: "InsurerReportsStore",
});

export const useInsurerReportsStore = createHook(InsurerReportsStore);
export const useInsurerReportsStoreState = createStateHook(InsurerReportsStore);
export const useInsurerReportsStoreActions =
  createActionsHook(InsurerReportsStore);
